import confirmDisablingRoomBookingAssistant from '../utils/confirmDisablingRoomBookingAssistant';
import { observer } from 'mobx-react-lite';
import type { CalendarId } from 'owa-graph-schema';
import { CalendarChannelPicker } from 'owa-calendar-channel-picker';
import { onCalendarIdChanged, onChannelChanged } from 'owa-calendar-compose-form-actions';
import canChangeCalendar from 'owa-calendar-compose-form-store/lib/utils/canChangeCalendar';
import { isPrimaryCalendarId } from 'owa-calendar-forms-common/lib/utils/isMeetingOnSecondaryCalendar';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import * as React from 'react';
import {
    roomBookingNotSupportedDialogTitle,
    secondaryCalendarNotSupportedDialogDescription,
} from './RoomBookingAssistant.locstring.json';
import ComposeSendSaveButton from './ComposeSendSaveButton';
import FullComposeInfoBar from './FullComposeInfoBar';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isSaveButtonInlineEnabled } from '../utils/getCommandBarUpdatesSDF';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

import styles from './InlineCommandBar.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface InlineCommandBarProps {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    formId: string;
    targetWindow: Window;
}

export default observer(function InlineCommandBar(props: InlineCommandBarProps) {
    const { event, colorScheme, formId, targetWindow } = props;
    const showSaveButtonInline =
        isSaveButtonInlineEnabled() && isHostAppFeatureEnabled('saveInFullCompose');
    const showCardLayout = isFeatureEnabled('mon-cal-showEditPaneInCards');

    const calendarIdChangedHandler = React.useCallback(
        (calendarId: CalendarId) => {
            if (isPrimaryCalendarId(calendarId)) {
                onCalendarIdChanged(calendarId, formId);
            } else {
                confirmDisablingRoomBookingAssistant(
                    event.IsRoomRequested,
                    roomBookingNotSupportedDialogTitle,
                    secondaryCalendarNotSupportedDialogDescription,
                    () => {
                        onCalendarIdChanged(calendarId, formId);
                    },
                    formId,
                    targetWindow
                );
            }
        },
        [event.IsRoomRequested, formId]
    );

    return (
        <div
            className={classNames(styles.container, {
                showSaveButtonInline: showSaveButtonInline,
                showCardLayout: showCardLayout,
            })}>
            {showCardLayout && (
                <FullComposeInfoBar
                    colorScheme={colorScheme}
                    formId={formId}
                    targetWindow={targetWindow}
                />
            )}
            <div
                className={classNames(styles.calendarRow, {
                    showCardLayout: showCardLayout,
                })}>
                <ComposeSendSaveButton formId={formId} />
                <CalendarChannelPicker
                    event={event}
                    colorScheme={colorScheme}
                    readonly={!canChangeCalendar(formId)}
                    onCalendarIdChanged={calendarIdChangedHandler}
                    onChannelChanged={onChannelChanged}
                    formId={formId}
                />
            </div>
        </div>
    );
});
